import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, SITE_URL } from '@/lib/api';
import { useSettings } from '@/lib/settings';
import SEO, { blogPostSchema, faqSchema } from '@/components/SEO';
import Breadcrumb from '@/components/Breadcrumb';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { ArrowLeft, Phone, ArrowRight } from 'lucide-react';
import { formatDate } from '@/lib/utils';
import { Helmet } from 'react-helmet-async';

export default function BlogDetail() {
    const { slug } = useParams();
    const { settings } = useSettings();
    const [post, setPost] = useState(null);
    const [related, setRelated] = useState([]);
    const [notFound, setNotFound] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get(`/public/blog/${slug}`).then(({ data }) => {
            setPost(data);
            // Fetch related
            if (data.related_posts?.length) {
                Promise.all(
                    data.related_posts.map((s) => api.get(`/public/blog/${s}`).then((r) => r.data).catch(() => null))
                ).then((arr) => setRelated(arr.filter(Boolean)));
            }
        }).catch(() => setNotFound(true)).finally(() => setLoading(false));
    }, [slug]);

    if (notFound) {
        return (
            <div className="sk-section">
                <div className="sk-container text-center">
                    <SEO title="Članak nije pronađen" noindex />
                    <h1 className="font-display text-4xl">Članak nije pronađen</h1>
                    <Button asChild className="mt-6"><Link to="/blog"><ArrowLeft className="mr-2 h-4 w-4" />Svi članci</Link></Button>
                </div>
            </div>
        );
    }

    if (loading || !post) {
        return (
            <div className="sk-section">
                <div className="sk-container space-y-4">
                    <Skeleton className="h-12 w-2/3" />
                    <Skeleton className="h-96 w-full rounded-2xl" />
                </div>
            </div>
        );
    }

    const seo = post.seo || {};
    const base = (settings?.site_url || SITE_URL).replace(/\/$/, '');
    const schemaParts = [blogPostSchema(post, settings)];
    if (post.faq?.length) schemaParts.push(faqSchema(post.faq));

    return (
        <>
            <SEO
                title={seo.meta_title || `${post.title} | Škobić Keramika`}
                description={seo.meta_description || post.excerpt}
                canonical={seo.canonical_url || `${base}/blog/${post.slug}`}
                image={seo.og_image || post.featured_image}
                type="article"
                schema={{ '@context': 'https://schema.org', '@graph': schemaParts.filter(Boolean) }}
            />
            <div className="sk-container pt-6">
                <Breadcrumb items={[
                    { name: 'Blog', url: '/blog' },
                    { name: post.title, url: `/blog/${post.slug}` },
                ]} />
            </div>

            <article className="sk-section pt-8">
                <div className="sk-container max-w-3xl">
                    {post.categories?.[0] && (
                        <Badge variant="secondary" className="bg-[color:var(--sk-surface-2)] text-foreground/80">{post.categories[0]}</Badge>
                    )}
                    <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">{post.title}</h1>
                    <div className="mt-4 flex items-center gap-3 text-xs uppercase tracking-[0.18em] text-foreground/60">
                        <span>{formatDate(post.published_at)}</span>
                        <span>•</span>
                        <span>{post.author}</span>
                    </div>
                </div>
                <div className="sk-container max-w-5xl mt-10">
                    <div className="aspect-[16/9] rounded-2xl overflow-hidden border border-border">
                        <img src={post.featured_image} alt={post.title} className="h-full w-full object-cover" fetchpriority="high" />
                    </div>
                </div>
                <div className="sk-container max-w-3xl mt-12 sk-prose" dangerouslySetInnerHTML={{ __html: post.content }} />

                {/* FAQ */}
                {post.faq?.length > 0 && (
                    <div className="sk-container max-w-3xl mt-12">
                        <h2 className="font-display text-3xl mb-4">Pitanja i odgovori</h2>
                        <Accordion type="single" collapsible className="space-y-3">
                            {post.faq.map((f, i) => (
                                <AccordionItem key={i} value={`f-${i}`} className="rounded-2xl border border-border bg-white px-5">
                                    <AccordionTrigger className="py-5 text-left text-base font-medium hover:no-underline">{f.question}</AccordionTrigger>
                                    <AccordionContent className="pb-5 text-sm text-foreground/70 leading-relaxed">{f.answer}</AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </div>
                )}

                {/* CTA */}
                <div className="sk-container max-w-3xl mt-12">
                    <div className="rounded-2xl border border-border bg-[color:var(--sk-surface-2)] p-8 text-center">
                        <h3 className="font-display text-2xl sm:text-3xl">{post.cta_text || 'Trebate keramičara u Poreču?'}</h3>
                        <div className="mt-5 flex flex-wrap gap-3 justify-center">
                            <Button asChild className="bg-[color:var(--sk-text)] text-white hover:bg-black rounded-xl">
                                <a href={settings?.phone_link || 'tel:+385977094158'}><Phone className="mr-2 h-4 w-4" />{settings?.phone || '+385 97 709 4158'}</a>
                            </Button>
                            <Button asChild variant="outline" className="rounded-xl bg-white"><Link to="/kontakt">Pošalji upit</Link></Button>
                        </div>
                    </div>
                </div>

                {/* Related posts */}
                {related.length > 0 && (
                    <div className="sk-container mt-16">
                        <h2 className="font-display text-3xl mb-6">Možda Vas zanima</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {related.map((r) => (
                                <Link key={r.id} to={`/blog/${r.slug}`} className="group overflow-hidden rounded-2xl border border-border bg-white">
                                    <div className="aspect-[16/10] overflow-hidden">
                                        <img src={r.featured_image} alt={r.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" loading="lazy" />
                                    </div>
                                    <div className="p-5">
                                        <h3 className="font-display text-lg tracking-tight">{r.title}</h3>
                                        <p className="mt-2 text-xs uppercase tracking-[0.18em] text-foreground/55">{formatDate(r.published_at)}</p>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                )}
            </article>
        </>
    );
}
